import React from "react";
import { Link } from "react-router-dom";
import { Database, ArrowRight, Inbox } from "lucide-react";

/**
 * LiveEmptyState — renders one of two flavours based on connector state:
 *
 *  1. Connector-CTA flavour (default) — "Connect a data source"
 *     Used when LIVE mode is on but NO data source is wired up yet.
 *
 *  2. Module-data flavour (variant="module") — "No <entity> yet"
 *     Used when a connector IS active and synced data exists somewhere,
 *     but THIS specific module's count is still zero.
 *
 * Pages should choose the variant based on `useLiveMode().hasLiveData` /
 * `isConnected` so we never tell the user to "connect a data source"
 * after they've already connected one.
 */
export default function LiveEmptyState({
  entity = "data",
  variant = "connector",       // "connector" | "module"
  title,
  message,
  ctaHref = "/admin?tab=connectors",
  ctaLabel = "Connect a data source",
  compact = false,
  testId,
  actions,                     // optional ReactNode rendered below message
}) {
  const isModule = variant === "module";
  const heading = title || (isModule ? `No ${entity} yet` : `No live ${entity} yet`);
  const body =
    message ||
    (isModule
      ? `Your data source is connected. ${entity[0].toUpperCase() + entity.slice(1)} will appear here as soon as activity is detected.`
      : `You're viewing LIVE mode — demo data is hidden. Connect Google Ads, Google Sheets, or upload a CSV to start seeing real ${entity} here.`);
  const Icon = isModule ? Inbox : Database;
  const tagText = isModule ? "AWAITING DATA" : "LIVE MODE";

  return (
    <div
      data-testid={testId || `live-empty-${entity}`}
      className={`bg-card border border-dashed border-border rounded-sm flex flex-col items-center justify-center text-center ${
        compact ? "py-8 px-6" : "py-14 px-8"
      }`}
    >
      <div className="w-12 h-12 rounded-sm bg-primary/10 text-primary flex items-center justify-center mb-4">
        <Icon className="w-5 h-5" strokeWidth={1.75} />
      </div>
      <div className="label-mono text-primary mb-2">{tagText}</div>
      <div className="font-heading text-lg tracking-tight mb-2">{heading}</div>
      <div className="text-sm text-muted-foreground max-w-md mb-5 leading-relaxed">
        {body}
      </div>
      {actions ? (
        <div className="flex flex-wrap items-center justify-center gap-2">{actions}</div>
      ) : (
        !isModule && (
          <Link
            to={ctaHref}
            data-testid={`live-empty-cta-${entity}`}
            className="inline-flex items-center gap-1.5 h-9 px-4 bg-primary text-primary-foreground text-sm font-semibold rounded-sm hover:bg-primary/90 transition-colors"
          >
            {ctaLabel}
            <ArrowRight className="w-4 h-4" strokeWidth={2} />
          </Link>
        )
      )}
    </div>
  );
}
